import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight, MapPin, Clock, Phone } from "lucide-react";
import { Button } from "../ui/button";
import { studio } from "../../mock";

const InfoItem = ({ icon: Icon, label, value, href, testId }) => {
  const content = (
    <div className="flex items-center gap-4 py-5 sm:px-6 first:sm:pl-0 last:sm:pr-0">
      <span className="flex h-10 w-10 shrink-0 items-center justify-center border border-foreground/15 text-primary">
        <Icon className="h-4 w-4" />
      </span>
      <div className="leading-tight">
        <p className="text-[10px] font-semibold uppercase tracking-[0.28em] text-muted-foreground">{label}</p>
        <p className="mt-1 text-sm font-semibold text-foreground">{value}</p>
      </div>
    </div>
  );
  if (href) {
    return (
      <a href={href} className="transition-colors hover:text-primary" data-testid={testId}>
        {content}
      </a>
    );
  }
  return <div data-testid={testId}>{content}</div>;
};

const HeroSection = () => {
  return (
    <section className="relative min-h-[100svh] overflow-hidden" id="welcome" data-testid="hero-section">
      {/* Background */}
      <div className="absolute inset-0">
        <img
          src={studio.images.hero}
          alt="Avalon Tattoo Collective studio"
          className="ken-burns h-full w-full object-cover object-center grayscale"
          fetchPriority="high"
        />
        <div className="absolute inset-0 bg-background/35" />
        <div className="absolute inset-0 bg-gradient-to-t from-background via-background/55 to-background/5" />
        <div className="absolute inset-y-0 left-0 w-1/2 bg-gradient-to-r from-background/70 to-transparent" />
      </div>

      {/* Content */}
      <div className="container-x relative flex min-h-[100svh] flex-col justify-end pb-12 pt-32 sm:pb-36 lg:pb-40">
        <p className="eyebrow hero-in hero-in-1 flex items-center gap-3">
          <span className="inline-block h-px w-8 bg-primary" />
          New Westminster, BC &mdash; Tattoo Studio &amp; Artist Collective
        </p>
        <h1 className="display hero-in hero-in-2 mt-5 text-[clamp(3.25rem,9vw,9.5rem)] uppercase leading-[0.84] tracking-wide">
          Avalon
          <br />
          Tattoo <span className="text-stroke">Collective</span>
        </h1>

        <div className="mt-8 grid gap-6 lg:grid-cols-12 lg:items-end">
          <p className="hero-in hero-in-3 max-w-xl text-base leading-relaxed text-foreground/80 md:text-lg lg:col-span-7">
            {studio.welcomeBody}
          </p>
          <div className="hero-in hero-in-4 flex flex-wrap gap-3 lg:col-span-5 lg:justify-end">
            <Button
              asChild
              size="lg"
              className="h-12 rounded-none px-7 text-[11px] font-semibold uppercase tracking-[0.2em]"
              data-testid="hero-cta-artists"
            >
              <Link to="/artists">
                Meet the Artists <ArrowRight className="ml-2 h-4 w-4" />
              </Link>
            </Button>
            <Button
              asChild
              size="lg"
              variant="outline"
              className="h-12 rounded-none border-foreground/30 bg-transparent px-7 text-[11px] font-semibold uppercase tracking-[0.2em] hover:border-foreground hover:bg-foreground hover:text-background"
              data-testid="hero-cta-guest"
            >
              <Link to="/guest-spots">
                Guest Spots <ArrowUpRight className="ml-2 h-4 w-4" />
              </Link>
            </Button>
          </div>
        </div>
      </div>

      {/* Scroll cue */}
      <div className="absolute bottom-28 right-6 hidden flex-col items-center gap-3 text-foreground/60 lg:flex lg:right-12">
        <span className="text-[10px] uppercase tracking-[0.3em] [writing-mode:vertical-rl]">Scroll</span>
        <span className="scroll-cue h-6 overflow-hidden" />
      </div>

      {/* Info strip */}
      <div className="relative border-t border-foreground/10 bg-background/55 backdrop-blur-md sm:absolute sm:inset-x-0 sm:bottom-0">
        <div className="container-x grid grid-cols-1 divide-y divide-foreground/10 sm:grid-cols-3 sm:divide-x sm:divide-y-0">
          <InfoItem icon={MapPin} label="Visit us" value={`${studio.address.line1}, ${studio.address.city}`} testId="hero-info-address" />
          <InfoItem icon={Clock} label="Hours" value={`${studio.hours.label} · ${studio.hours.open} – ${studio.hours.close}`} testId="hero-info-hours" />
          <InfoItem icon={Phone} label="Call for details" value={studio.phone} href={studio.phoneHref} testId="hero-info-phone" />
        </div>
      </div>
    </section>
  );
};

export default HeroSection;
